/**
 * Shared contracts for every subsystem.
 *
 * This file is the single source of truth for cross-module interfaces. Systems
 * talk to each other through these types and through the typed event bus in
 * `core/events.ts` — they should not import each other's implementation files.
 *
 * Coordinate conventions
 * ----------------------
 *  - World space is three.js standard: +X right, +Y up, -Z forward.
 *  - The world is laid out on a 2D grid in the XZ plane. Grid cell (cx, cy)
 *    maps to world (cx * cellSize, cy * cellSize) at its *corner*; use
 *    `Grid.cellToWorld` for the center.
 *  - `yaw` is rotation about +Y in radians. yaw = 0 looks down -Z.
 *  - All distances are metres, all times are seconds, all angles radians.
 */

import type * as THREE from 'three';
import type { PropIndex } from './props';

/* ------------------------------------------------------------------ *
 * Math
 * ------------------------------------------------------------------ */

/** A point on the horizontal plane. */
export interface Vec2 {
  x: number;
  z: number;
}

/** An integer grid coordinate. */
export interface Cell2 {
  cx: number;
  cy: number;
}

/** Deterministic RNG. All world generation must route through one of these. */
export interface RNG {
  /** Uniform float in [0, 1). */
  next(): number;
  /** Uniform float in [min, max). */
  range(min: number, max: number): number;
  /** Uniform integer in [min, max] inclusive. */
  int(min: number, max: number): number;
  /** True with probability p. */
  chance(p: number): boolean;
  /** Uniformly pick one element. Throws on an empty array. */
  pick<T>(items: readonly T[]): T;
  /** In-place Fisher–Yates. Returns the same array. */
  shuffle<T>(items: T[]): T[];
  /** A new independent stream derived from this one. */
  fork(salt?: number): RNG;
  /** The seed this stream was created with. */
  readonly seed: number;
}

/* ------------------------------------------------------------------ *
 * World grid
 * ------------------------------------------------------------------ */

/** What occupies a single grid cell. Stored as a Uint8Array of these values. */
export enum Cell {
  /** Outside the level. Solid, never rendered as a room wall. */
  Void = 0,
  /** Solid wallpaper-covered wall, full height. */
  Wall = 1,
  /** Walkable floor. */
  Floor = 2,
  /** Walkable floor with a square support pillar in the middle. Blocks the centre. */
  Pillar = 3,
  /** Walkable floor covered in shallow water (slower, splashy footsteps). */
  Water = 4,
  /** Walkable floor with damp/soaked carpet. Cosmetic + audio variation. */
  Damp = 5,
  /** A doorway gap in a wall run. Walkable; renderer may add a frame. */
  Doorway = 6,
  /** Walkable, but the ceiling above is missing/open. Used for shafts and light wells. */
  Open = 7,
}

/** Cell values a player or entity may stand in. */
export const WALKABLE: readonly Cell[] = [
  Cell.Floor,
  Cell.Pillar,
  Cell.Water,
  Cell.Damp,
  Cell.Doorway,
  Cell.Open,
];

/** The static level occupancy map. Implemented in `world/grid.ts`. */
export interface Grid {
  readonly w: number;
  readonly h: number;
  /** Width/depth of one cell in metres. */
  readonly cellSize: number;
  /** Row-major `cy * w + cx`, length `w * h`. */
  readonly cells: Uint8Array;

  /** Cell value, or `Cell.Void` when out of bounds. */
  at(cx: number, cy: number): Cell;
  set(cx: number, cy: number, v: Cell): void;
  inBounds(cx: number, cy: number): boolean;
  /** True when the cell blocks movement entirely (Void / Wall). */
  isSolid(cx: number, cy: number): boolean;
  /** True when an entity may stand here. */
  isWalkable(cx: number, cy: number): boolean;

  /** World position -> containing cell. Floors, so it works for negatives. */
  worldToCell(wx: number, wz: number): Cell2;
  /** Cell -> world position of the cell's centre. */
  cellToWorld(cx: number, cy: number): Vec2;

  /** Convenience: is the world point inside a solid cell? */
  isSolidAt(wx: number, wz: number): boolean;
}

/** A labelled rectangular area produced by the generator, in cell coordinates. */
export interface Region {
  id: number;
  /** Inclusive min corner. */
  x: number;
  y: number;
  /** Size in cells. */
  w: number;
  h: number;
  kind: RegionKind;
  /** Centre of the region in world space. */
  center: Vec2;
}

export type RegionKind =
  | 'room' // ordinary mono-yellow room
  | 'hall' // long straight corridor
  | 'pillar' // wide open area on a pillar grid
  | 'closet' // tiny dead-end, good for supplies
  | 'flooded' // standing water
  | 'atrium' // large open space, taller ceiling
  | 'warren'; // dense tangle of tiny rooms

/* ------------------------------------------------------------------ *
 * Levels
 * ------------------------------------------------------------------ */

/** Colour and mood knobs the renderer reads. Hex numbers, e.g. 0xd9cf6d. */
export interface LevelPalette {
  wall: number;
  wallTrim: number;
  carpet: number;
  carpetStain: number;
  ceiling: number;
  lightTint: number;
  fog: number;
  /** Ambient light colour. */
  ambient: number;
}

/** Authored, static description of one backrooms level. See `world/levels.ts`. */
export interface LevelSpec {
  /** Stable id, e.g. `'level-0'`. */
  id: string;
  /** Display name, e.g. `'Level 0'`. */
  name: string;
  /** Flavour subtitle, e.g. `'The Lobby'`. */
  subtitle: string;
  /** One or two lines shown on the level card when entering. */
  blurb: string;

  gridW: number;
  gridH: number;
  cellSize: number;
  /** Floor-to-ceiling height in metres. */
  ceilingHeight: number;

  palette: LevelPalette;
  fogNear: number;
  fogFar: number;
  /**
   * Metres past which the level's geometry is switched off, overriding
   * `TUNING.render.cullDistance`.
   *
   * The default suits a building made of rooms: you can never see further than
   * the far wall, so paying to draw anything past forty metres is paying for
   * nothing. A level with sky over it is the opposite case — Level 94's whole
   * layout is arranged around a twelve-metre landmark that is only worth having
   * if it can be seen from across the neighbourhood — and there the cull is what
   * decides whether the level has a premise. Costs draw calls in direct
   * proportion, so raise it only where the level is mostly outdoors.
   */
  viewDistance?: number;
  /** Base ambient light intensity, 0..1. */
  ambientIntensity: number;

  /** Roughly one ceiling light per this many walkable cells. */
  lightSpacing: number;
  /** Fraction of lights that are dead or flickering, 0..1. */
  brokenLightChance: number;

  /** Expected number of supply caches on the level. */
  supplyCount: number;
  /** Items that can appear here, with relative weights. */
  supplyTable: Array<{ kind: ItemKind; weight: number }>;
  /**
   * Items this level always contains, whatever the weighted draw says. They are
   * placed in the scored (non-cache) spots so they sit on the route through the
   * level rather than behind a wrong turn. This is how the flashlight gets into
   * the player's hands: a weight in `supplyTable` would only make it likely.
   */
  guaranteedItems?: ItemKind[];

  /**
   * Entities allowed on this level.
   *
   * Read twice, by two passes with different rules, and they do not agree:
   * `world/gen/placement.ts` picks the level's opening spawns *uniformly* from
   * this list, while the director's later spawns are weighted by
   * `SPAWN_WEIGHT`. So a kind listed here appears at load however small its
   * weight is, and a roster is a statement about what belongs on the floor
   * rather than a knob. Keep it to what the lore puts on the level; the
   * frequency knobs are `entityBudget` and `threatScale`.
   */
  entityKinds: EntityKind[];
  /** Simultaneous entity cap. 0 or 1 keeps encounters rare. */
  entityBudget: number;
  /** Multiplier on the director's spawn pressure. 1 = baseline. */
  threatScale: number;

  /** How many exits to place. Finding one lets the player descend. */
  exitCount: number;

  /**
   * The flashlight does not burn charge on this level, and batteries are not
   * needed. For floors where the darkness *is* the level: metering the beam
   * there turns being in the dark into an inventory problem instead.
   */
  freeFlashlight?: boolean;
  /** Water covers the floor of flooded regions at this depth in metres. */
  waterDepth?: number;
  /** Generator profile to use. */
  generator: GeneratorKind;
}

/**
 * Which layout algorithm carves a level, and — just as importantly — which
 * surfaces, prop catalogue, footstep and exit vocabulary it inherits.
 *
 * Adding one is a wide change on purpose. It keys exhaustive `Record`s in
 * `world/gen/placement.ts` (`EXIT_KINDS`), `world/gen/propTables.ts` (`THEMES`,
 * `CAMP_KIT`, `DENSITY`), `player/footsteps.ts` and `game.ts` (`AMBIENCE_FOR`),
 * and non-exhaustive `switch`es in `render/textures.ts`, `render/materials.ts`,
 * `render/propkit.ts` and `render/propwalls.ts`. The `Record`s will not compile
 * until the new kind is answered; the `switch`es have `default:` arms, so a new
 * kind that is not named in them silently gets the Lobby's wallpaper. Check all
 * four render files by hand.
 */
export type GeneratorKind =
  | 'lobby' // Level 0: partitioned mono-yellow rooms
  | 'warehouse' // Level 1: big spans, concrete, pillar grids
  | 'pipes' // Level 2: tight service corridors
  | 'poolrooms' // Level 37: tiled halls and shallow water
  | 'hub' // small hand-shaped safe area
  | 'funrooms' // The Funrooms: party rooms whose cheerfulness is entirely wrong
  | 'motion' // Level 94: a childlike neighbourhood under a stop-motion sky
  | 'offices' // Level 4: a real office floor plate, lit by its windows
  | 'station' // Level 3: switchgear halls, trunk corridors and service crawls
  | 'hotel' // Level 5: double-loaded corridors and three hundred guest rooms
  | 'lightsout' // Level 6: a bare concrete corridor lattice with eleven lights on it
  | 'city'; // Level 11: a street grid under an overcast sky that never changes

/** Where the player can leave the level. */
export interface ExitPoint {
  id: number;
  pos: Vec2;
  /** How it reads to the player. */
  kind: 'noclip' | 'stairwell' | 'hole' | 'door' | 'slide';
  /** Radius in metres the player must enter to trigger it. */
  radius: number;
  /** Index into the level order this exit leads to. -1 means "the next one". */
  target: number;
}

/** A ceiling light fixture placed by the generator. */
export interface LightSpot {
  pos: Vec2;
  /** 'on' burns steady, 'flicker' stutters, 'dead' emits nothing. */
  state: 'on' | 'flicker' | 'dead';
  /** Per-fixture phase offset so flickers do not sync up. */
  phase: number;
}

/** A pickup the generator scattered. */
export interface ItemSpot {
  id: number;
  pos: Vec2;
  kind: ItemKind;
  /** How many uses/units this spot holds. */
  amount: number;
}

/** An initial or director-chosen entity spawn location. */
export interface EntitySpawn {
  pos: Vec2;
  kind: EntityKind;
}

/**
 * Scenery. A prop with a `footprint` is a real obstacle: the player collides
 * with it and entities route around it, so it may stand anywhere the placer can
 * prove a way past it still exists. A prop without one is walked through, and
 * so may only go where nobody would try to walk — against walls, in corners, in
 * dead ends, on the ceiling, or flat on the floor.
 *
 * The two must agree with the model. A box smaller than what is drawn is an
 * object the player walks into the middle of; a box larger is an invisible
 * wall. `world/gen/propTables.ts` holds the measured numbers.
 *
 * Their real job is navigation. An empty grid of identical rooms is a random
 * walk; a room with an overturned chair in it is a room you have been in
 * before. Landmark props (graffiti, tallies, arrows, anything a previous
 * survivor left) should be rare and memorable rather than scattered evenly.
 */
export interface PropSpot {
  id: number;
  pos: Vec2;
  /** Facing, radians. Wall-hugging props should face out of their wall. */
  yaw: number;
  kind: PropKind;
  /** Height above the floor. 0 for floor props; used for wall and ceiling props. */
  height: number;
  /** Uniform scale multiplier, for size variety within a kind. */
  scale: number;
  /**
   * Per-prop deterministic variation seed, so a view can vary without its own RNG.
   *
   * For `arrow` the low bit is not free: **`variant & 1` is the side the arrow
   * points** — 0 for the prop's left, 1 for its right, in the plane of the wall
   * it is painted on, as seen by a player facing it. The placer sets it so that
   * following the arrow gets you closer to an exit, which is the entire reason
   * arrows exist. A view that ignores the bit still renders; it just lies.
   */
  variant: number;
  /**
   * Collision footprint, in the prop's own local space, or omitted when the
   * player walks through it (decals, papers, ceiling fittings, small debris).
   *
   * Anything big enough to read as an obstacle must have one. A bathtub in the
   * middle of a room that the player strolls through is worse than no bathtub.
   */
  footprint?: PropFootprint;
}

/**
 * An axis-aligned box in the prop's local frame, rotated by `PropSpot.yaw`.
 *
 * The box has no base elevation: it always starts at the floor. So only a prop
 * that stands on the floor may carry one — a ceiling fitting or a high wall
 * mount with a footprint would block the floor underneath itself, where there
 * is plainly nothing. `soffit`, `chandelier`, `hanging-light`, `wall-clock` and
 * the rest of the overhead catalogue deliberately have none.
 */
export interface PropFootprint {
  /** Half-extent along local X (across the prop), metres. */
  halfX: number;
  /** Half-extent along local Z (through the prop), metres. */
  halfZ: number;
  /**
   * Height in metres. Below `TUNING.player.stepOverHeight` the player steps
   * over it, so low debris can still carry a footprint for entities to route
   * around without the player catching on it.
   */
  height: number;
}

export type PropKind =
  // Lobby
  | 'chair'
  | 'boxes'
  | 'cabinet'
  | 'bucket'
  | 'papers'
  | 'vending'
  | 'payphone'
  | 'hanging-light'
  | 'ceiling-stain'
  // Warehouse
  | 'pallet'
  | 'crate'
  | 'shelving'
  | 'cone'
  | 'drum'
  | 'handtruck'
  // Pipes
  | 'pipe-run'
  | 'valve'
  | 'junction-box'
  | 'ladder'
  | 'puddle'
  // Poolrooms
  | 'pool-ladder'
  | 'bench'
  | 'float-ring'
  | 'drain'
  | 'towel'
  // Landmarks left by people. Rare, and the reason the maze is navigable.
  | 'graffiti'
  | 'tally-marks'
  | 'arrow'
  | 'sign'
  | 'camp'
  /*
   * Architecture that does not make sense.
   *
   * The building is the thing that is wrong, and it should be wrong in ways a
   * builder could have done on purpose and then abandoned: a wall that stops,
   * a door standing on its own, three steps up to nothing. These are structural
   * enough to need a `footprint`, and they are the main reason a room stops
   * looking like a rectangle.
   */
  | 'half-wall' // waist-height wall stub crossing part of a room
  | 'stub-wall' // full-height wall that simply stops, ending in mid-air
  | 'free-doorframe' // a doorframe standing alone, leading from nothing to nothing
  | 'false-door' // a door set in a wall that has never opened
  | 'soffit' // a bulkhead hanging from the ceiling, crossing the room
  | 'column-cluster' // three columns set far too close together
  | 'stairs-nowhere' // four steps up to a blank wall
  | 'partition' // a freestanding office cubicle divider
  | 'inner-window' // an interior window into a room that is dark
  /*
   * Objects that should not be here, presented without comment. Mundane and
   * out of place beats surreal and decorative — a bathtub in an office is
   * unsettling, a floating clock is set dressing.
   */
  | 'bathtub'
  | 'tv-stack'
  | 'phone-booth'
  | 'shopping-cart'
  | 'mattress'
  | 'piano'
  | 'fish-tank'
  | 'chandelier'
  | 'mannequin'
  | 'chair-pile'
  | 'door-pile'
  | 'desk'
  | 'bookshelf'
  | 'mirror'
  | 'dead-plant'
  | 'bed-frame'
  | 'sofa'
  | 'standing-lamp'
  | 'wall-clock'
  | 'sink'
  | 'filing-tower'
  | 'carpet-roll'
  | 'hydrant'
  | 'bicycle'
  /**
   * One shoe, still laced, in the middle of a corridor. The only prop in the
   * game that is a person rather than their equipment, and it says so without
   * any gore — which is the register this game keeps.
   */
  | 'shoe'
  /**
   * The mouth of a slide down to another level. Placed by the generator at an
   * `ExitPoint` of kind `'slide'`, so the thing you can see and the thing you
   * can use are the same object. It is deliberately the largest and loudest
   * prop in the game: a slide the player never finds is a slide that may as
   * well not exist.
   */
  | 'slide'
  /*
   * The Funrooms. A soft-play centre and a function suite in the same building,
   * both left running. The register is a children's party that finished a long
   * time ago and was never cleared away — bright, sticky, and entirely empty.
   */
  | 'play-structure' // soft-play frame: platforms, netting, padded posts
  | 'play-slide' // a moulded plastic playground slide, going nowhere
  | 'ball-pit' // low walled pit, still full
  | 'crawl-tube' // a run of translucent tube on legs
  | 'soft-blocks' // foam play shapes, stacked or scattered
  | 'balloons' // a bunch on a weight, most of them down
  | 'streamers' // paper chains and ceiling streamers
  | 'banner' // a party banner strung across the room
  | 'party-table' // trestle table, paper cups, a cake nobody cut
  | 'arcade-cabinet' // dead, screen off
  | 'bouncy-castle' // deflated over its own blower
  | 'confetti' // trodden into the carpet
  /*
   * Level 94. A stop-motion neighbourhood: too clean, too saturated, built at
   * about nine tenths of the size it ought to be.
   */
  | 'picket-fence'
  | 'mailbox'
  | 'lamp-post'
  | 'shrub'
  | 'garden-tree'
  | 'swing-set'
  | 'parked-car'
  | 'water-tower' // the landmark at the middle of the level
  | 'laundry-line'
  /*
   * The three that make a Level 94 building a house rather than a block. Unlike
   * everything else in this list they are fitted to a *rectangle of cells*: the
   * variant encodes the building's size, so a roof for a five-by-four house is
   * not a scaled roof for a four-by-four one. `world/gen/motion.ts` places them
   * itself, against the plan; they are deliberately absent from `THEMES`,
   * `oddityKinds`, `hoardKinds` and `CAMP_KIT`, and the general placer must
   * never draw one.
   */
  | 'house-hill' // the rounded green mound the building stands on
  | 'house-roof' // pitch, eaves, verge, and a chimney with two pots
  | 'house-trim'; // one bay of a wall: windows, an oriel, or the porch hood

/** The finished, playable level. Produced by `world/generator.ts`. */
export interface GeneratedLevel {
  spec: LevelSpec;
  grid: Grid;
  regions: Region[];
  /** Where the player starts. */
  spawn: Vec2;
  /** Facing direction at spawn, radians. */
  spawnYaw: number;
  exits: ExitPoint[];
  lights: LightSpot[];
  items: ItemSpot[];
  entitySpawns: EntitySpawn[];
  /** Scenery. Those with a `footprint` are solid; see `PropSpot`. */
  props: PropSpot[];
  /**
   * Spatial index over the solid props, built once with the level. Both the
   * player's collision and the entities' steering read it, so a prop that
   * blocks one blocks the other.
   */
  propIndex: PropIndex;
  /** Seed the level was generated from, for reproducibility. */
  seed: number;
  /**
   * Distance in cells from the spawn, computed by BFS over walkable cells.
   * Row-major like `Grid.cells`. 0xffff means unreachable. Used to place
   * supplies and exits far from spawn, and by the director to keep entities
   * away from the player's arrival point.
   */
  distanceFromSpawn: Uint16Array;
}

/* ------------------------------------------------------------------ *
 * Items and survival
 * ------------------------------------------------------------------ */

export type ItemKind =
  | 'almond-water' // the largest sanity restore in the game. The canonical find.
  | 'water-bottle' // a small comfort; a little sanity
  | 'granola' // a small comfort; a little sanity
  | 'canned-food' // health and a little sanity, needs no tool
  | 'candy' // a small comfort; a little sanity
  | 'battery' // recharges the flashlight
  | 'flashlight' // the tool itself; only ever picked up once
  | 'bandage' // restores health
  | 'pills' // restores sanity
  | 'note' // lore. Reading it restores a little sanity.
  | 'key'; // opens locked doors, if a level uses them

/** Static description of an item kind. See `survival/items.ts`. */
export interface ItemDef {
  kind: ItemKind;
  /** Display name, e.g. 'Almond Water'. */
  label: string;
  /** One-line flavour shown on pickup or in the inventory. */
  description: string;
  /** Can it be consumed from the inventory? */
  consumable: boolean;
  /** How many fit in one inventory slot. */
  stackSize: number;
  /** Applied on consume. Values are absolute points on the 0..100 stats. */
  effects: Partial<Record<StatKey, number>>;
  /** Tint used for the world pickup mesh and the HUD glyph. */
  color: number;
}

export type StatKey = 'health' | 'sanity';

/**
 * The player's live survival numbers. Every value is 0..100.
 *
 * There is deliberately no hunger or thirst. Being lost is the threat; a clock
 * that kills you for standing still turns exploring the building into shopping
 * for it. Sanity is the stat the game is actually about.
 */
export interface SurvivalStats {
  health: number;
  /** 100 = composed, 0 = gone. */
  sanity: number;
}

/** One inventory slot. */
export interface InventorySlot {
  kind: ItemKind;
  count: number;
}

/** Something in the world the player can look at and press E on. */
export interface Interactable {
  id: number;
  pos: Vec2;
  /** Metres. The player must be within this to interact. */
  radius: number;
  /** Verb shown in the prompt, e.g. 'Take'. */
  verb: string;
  /** Noun shown in the prompt, e.g. 'Almond Water'. */
  label: string;
  /** Runs when the player interacts. Return false to keep the interactable alive. */
  onInteract(): boolean | void;

  /* -- Aiming, for a pointer rather than a crosshair -- */

  /**
   * How to point *at* this thing, as opposed to how close you must be to touch
   * it, which is what `radius` says.
   *
   * A crosshair needs neither: `pos` and the player's own facing settle it, and
   * everything above is all the flat game has ever needed. A hand controller or
   * a gaze ray needs all three, because it is a line in space rather than a
   * cone from the eye — it has to know how high off the floor the thing sits,
   * how much of a target it presents, and how far down the ray it may be
   * reached for. Whoever creates the interactable is the one that knows.
   *
   * All optional. Left out, a consumer should treat the thing as a supply lying
   * on the floor and use `radius` as the reach, which is what a supply is.
   */
  aimY?: number;
  /** Metres of slop around the aim point, before any angular tolerance. */
  aimRadius?: number;
  /**
   * Metres past which a ray may not reach this, where that is not `radius`.
   *
   * Worth having separately because pointing is not reaching. A supply is
   * comfortably taken from across a room; a way *down* is the only
   * unrecoverable act in a run, so it deliberately does not answer a ray from
   * the far side of one.
   */
  aimReach?: number;
}

/* ------------------------------------------------------------------ *
 * Player
 * ------------------------------------------------------------------ */

/** Live player state, owned by `player/controller.ts` and read by everyone. */
export interface PlayerState {
  /** Feet position. `pos.y` is not stored here; the player never leaves the floor plane. */
  pos: Vec2;
  /** Eye height above the floor, including head bob and crouch. */
  eyeHeight: number;
  /** Horizontal look angle, radians. */
  yaw: number;
  /** Vertical look angle, radians, clamped to roughly ±85°. */
  pitch: number;
  /** Current horizontal velocity in m/s. */
  vel: Vec2;
  /** Metres per second the player is actually moving. */
  speed: number;
  crouching: boolean;
  sprinting: boolean;
  /** True while the player is holding still and crouched — entities notice them less. */
  hidden: boolean;
  /** Set by the flashlight system. */
  flashlightOn: boolean;
  /** 0..100. */
  flashlightCharge: number;
  /** Blocks input while true (cutscenes, death, level transitions). */
  frozen: boolean;
  /** How much noise the player is making. Entities hear this. 0..1. */
  noise: number;
  /**
   * Multiplier on movement speed. Written by the survival system (injury only),
   * read by the player controller. 1 = unimpaired.
   */
  speedScale: number;
}

/* ------------------------------------------------------------------ *
 * Entities
 * ------------------------------------------------------------------ */

/**
 * Every walking thing in the game.
 *
 * All of these except `hollow` are documented Backrooms entities, and each is
 * placed on the levels the lore puts it on — which is why the Lobby has
 * Bacteria in it and the Funrooms has Partygoers. `hollow` is deliberately not
 * lore: it is what a failing mind puts at the end of a corridor, and it is
 * bought with sanity rather than with the director's threat budget.
 *
 * Two invented kinds, `skin-stealer` and `wretch`, were retired when the
 * canonical roster landed — the stalking is the faceling's now and the corridor
 * blocking is the partygoer pack's. Do not re-add an entity here without a
 * behaviour, a def, a view, a spawn weight, a threat row and a sanity row; the
 * exhaustive `Record<EntityKind, …>`s in `entities/behaviors/index.ts`,
 * `entities/defs.ts`, `entities/director.ts`, `entities/system.ts` and
 * `survival/sanity.ts` are what make that a compile error rather than a
 * silently mindless body standing in a corridor.
 */
export type EntityKind =
  | 'hound' // fast, blind, hunts by sound. The real threat.
  | 'smiler' // a grin in the dark. Mostly dread; punishes staring.
  | 'clump' // stationary hazard growth, damages on contact
  | 'hollow' // hallucination spawned by low sanity; harmless, vanishes when looked at twice
  | 'bacteria' // Level 0's own: a wired skeletal thing that screams on sight and accelerates as it chases
  | 'faceling' // Entity 9: a tall featureless humanoid. Closes only while unobserved
  | 'deathmoth' // Entity 4: drawn to light, and to whoever is carrying it
  | 'partygoer' // Entity 67: the Funrooms' hosts, and they have been waiting
  | 'centipede'; // Sticky-Centipede: yellow, red-striped, and at home in the water

export type EntityMode =
  | 'dormant' // exists but inactive/far away
  | 'wander' // drifting through the level
  | 'patrol' // moving along a route
  | 'investigate' // heading to a heard noise
  | 'stalk' // aware of the player, keeping distance
  | 'chase' // actively pursuing
  | 'attack' // in contact
  | 'flee' // retreating (light, sanity items, scripted)
  | 'dead';

/** Live state for one entity instance. */
export interface EntityState {
  id: number;
  kind: EntityKind;
  pos: Vec2;
  yaw: number;
  vel: Vec2;
  mode: EntityMode;
  health: number;
  /** 0..1 confidence that it knows where the player is. */
  awareness: number;
  /** Last place it believes the player to be. */
  lastKnown: Vec2 | null;
  /** Seconds spent in the current mode. */
  modeTime: number;
  /** Seconds since spawn. */
  age: number;
  /** Distance to the player in metres, refreshed each frame by the director. */
  distToPlayer: number;
  /** True when the player has line of sight to it right now. */
  visible: boolean;
}

/** Static per-kind tuning. See `entities/defs.ts`. */
export interface EntityDef {
  kind: EntityKind;
  label: string;
  /** Metres per second. */
  walkSpeed: number;
  chaseSpeed: number;
  /** Metres. How far it can see the player in lit conditions. */
  sightRange: number;
  /** Radians, total field of view. */
  fov: number;
  /** Metres. How far it can hear the player at full noise. */
  hearingRange: number;
  /** Metres. Contact distance for an attack. */
  attackRange: number;
  /** Damage per hit. */
  damage: number;
  /** Seconds between attacks. */
  attackCooldown: number;
  /** Hit points. Infinity for unkillable. */
  health: number;
  /** Collision radius in metres. */
  radius: number;
  /** Height in metres, for the mesh and for line-of-sight checks. */
  height: number;
  /** Sanity drained per second while visible and near. */
  sanityDrain: number;
  /** How brightly lit an area has to be before it refuses to enter. 0 = fearless. */
  lightAversion: number;
}

/* ------------------------------------------------------------------ *
 * Audio
 * ------------------------------------------------------------------ */

export type SfxName =
  | 'step-carpet'
  | 'step-concrete'
  | 'step-water'
  | 'step-tile'
  | 'pickup'
  | 'consume-drink'
  | 'consume-food'
  | 'heartbeat'
  | 'hurt'
  | 'death'
  | 'flashlight-click'
  | 'flashlight-dying'
  | 'door'
  | 'noclip'
  | 'descend'
  | 'light-buzz-pop'
  | 'distant-thud'
  | 'distant-scrape'
  | 'distant-voice'
  /**
   * A slide, heard from across the room: a big hollow plastic tube resonating
   * at nothing. Its own name rather than a borrowed `distant-scrape`, because
   * that one is the director's fakeout — a player who has learned that a scrape
   * means something is moving must not hear the way down and think it is.
   */
  | 'slide-settle'
  | 'hound-growl'
  | 'hound-scream'
  | 'hound-step'
  | 'smiler-breath'
  | 'smiler-laugh'
  /**
   * A pack of Partygoers running: several sets of feet, loose and uneven. Built
   * from the carpet-step synth with the numbers nudged, so no two land the same
   * way. Deliberately not `hound-step` — that one is the game's taught warning.
   */
  | 'partygoer-step'
  /** The Sticky-Centipede's lunge: a wet body and a lot of displaced water. */
  | 'centipede-surge'
  | 'whisper'
  | 'ui-select'
  | 'ui-confirm'
  | 'ui-back';

/** Everything the rest of the game may ask of the audio engine. */
export interface AudioAPI {
  /** Must be called from a user gesture. Safe to call repeatedly. */
  resume(): Promise<void>;
  readonly ready: boolean;

  /** Fire a one-shot. `pos` omitted means "at the listener", i.e. non-positional. */
  play(name: SfxName, opts?: { pos?: Vec2; gain?: number; rate?: number }): void;

  /** Move the listener. Called every frame by the game loop. */
  setListener(pos: Vec2, yaw: number): void;

  /**
   * Cross-fade the ambience bed. Called on level load.
   * `intensity` 0..1 scales the fluorescent hum and room tone.
   */
  setAmbience(profile: AmbienceProfile, intensity?: number): void;

  /**
   * Drives the adaptive layer: dread rises with entity proximity, sanity loss
   * and darkness. 0 = calm exploration, 1 = a hound is on you.
   */
  setTension(value: number): void;

  /** Master volume 0..1, persisted by the settings system. */
  setMasterVolume(v: number): void;
  setMuted(muted: boolean): void;

  /** Per-frame update for continuous/procedural layers. */
  update(dt: number, ctx: GameContext): void;

  /**
   * Release the AudioContext. Browsers cap how many a page may hold, so a
   * `Game` that is rebuilt (a dev-server reload, a restart-by-reconstruct)
   * must not leak the old one.
   */
  dispose(): void;
}

export type AmbienceProfile = 'lobby' | 'warehouse' | 'pipes' | 'poolrooms' | 'menu' | 'silence';

/* ------------------------------------------------------------------ *
 * Events
 * ------------------------------------------------------------------ */

/**
 * The complete event vocabulary. Systems communicate through this rather than
 * by importing each other. Add new events here first.
 */
export interface GameEvents {
  'player:step': { surface: 'carpet' | 'concrete' | 'water' | 'tile'; speed: number; pos: Vec2 };
  'player:hurt': { amount: number; source: EntityKind | 'fall' | 'clump' };
  'player:died': { cause: string };
  'player:sprint': { on: boolean };
  'player:crouch': { on: boolean };
  'player:noise': { pos: Vec2; loudness: number };

  'item:pickup': { kind: ItemKind; amount: number };
  'item:consume': { kind: ItemKind };
  'item:full': { kind: ItemKind };
  'inventory:changed': { slots: readonly InventorySlot[] };

  'stats:changed': { stats: Readonly<SurvivalStats> };
  'stats:critical': { stat: StatKey };

  'flashlight:toggle': { on: boolean };
  'flashlight:depleted': Record<string, never>;
  /**
   * The survival system emits this when a battery is used; the player
   * controller owns `flashlightCharge` and applies the top-up.
   */
  'flashlight:recharge': { amount: number };

  'entity:spawned': { id: number; kind: EntityKind; pos: Vec2 };
  'entity:sighted': { id: number; kind: EntityKind; dist: number };
  /**
   * Sanity pressure from one entity, in points **per second**. A rate rather
   * than a delta so it stays meaningful between the behaviour tick that reports
   * it and the frames that spend it.
   *
   * The entity system knows the behaviour context (a smiler being stared at, a
   * clump you are standing in); the survival system owns `SurvivalStats.sanity`
   * and is its only writer. This event is the seam, so entity dread is applied
   * exactly once and by the system that owns the number.
   */
  'entity:dread': { id: number; kind: EntityKind; rate: number };
  'entity:mode': { id: number; kind: EntityKind; mode: EntityMode; prev: EntityMode };
  'entity:attack': { id: number; kind: EntityKind; damage: number };
  'entity:despawned': { id: number; kind: EntityKind };
  /** A harmless scare: a noise with nothing behind it, or a shape that is gone when looked at. */
  'entity:fakeout': { pos: Vec2; flavor: 'sound' | 'glimpse' | 'shadow' };
  /**
   * The slide surge has escalated far enough that the player is owed an
   * explanation for it. Fired at most once per run. The surge decides when
   * — it is the only thing that knows its own intensity — and `game.ts` says
   * it, with the rest of the once-per-run teaching lines.
   */
  'surge:warn': Record<string, never>;

  'level:loaded': { spec: LevelSpec; index: number };
  'level:exitFound': { exit: ExitPoint };
  'level:descend': { from: number; to: number };

  'ui:toast': { text: string; tone?: 'info' | 'good' | 'bad' };
  'ui:subtitle': { text: string; ms?: number; speaker?: string };
  'ui:prompt': { text: string | null };
  /** `id` is stable per authored note, so the save system can key a journal off it. */
  'ui:note': { id: string; title: string; body: string };

  'game:pause': Record<string, never>;
  'game:resume': Record<string, never>;
  'game:over': { reason: string; survived: number; depth: number };
  'game:start': { seed: number };
}

/** Typed pub/sub. Implemented in `core/events.ts`. */
export interface EventBus {
  on<K extends keyof GameEvents>(type: K, fn: (payload: GameEvents[K]) => void): () => void;
  once<K extends keyof GameEvents>(type: K, fn: (payload: GameEvents[K]) => void): () => void;
  off<K extends keyof GameEvents>(type: K, fn: (payload: GameEvents[K]) => void): void;
  emit<K extends keyof GameEvents>(type: K, payload: GameEvents[K]): void;
  clear(): void;
}

/* ------------------------------------------------------------------ *
 * Input
 * ------------------------------------------------------------------ */

export type ActionName =
  | 'forward'
  | 'back'
  | 'left'
  | 'right'
  | 'sprint'
  | 'crouch'
  | 'jump'
  | 'interact'
  /**
   * Hit another player. Only ever means anything in a shared world: held, not
   * tapped, and the wind-up is drawn on the person you are aiming at. Bound to
   * V and to the right mouse button — never to the left one, which is already
   * `interact` and would turn every attempt to pick up a granola bar into a
   * swing at whoever is standing behind it.
   */
  | 'attack'
  | 'flashlight'
  | 'inventory'
  | 'map'
  | 'pause'
  | 'use1'
  | 'use2'
  | 'use3'
  | 'use4';

/** Implemented in `core/input.ts`. */
export interface InputAPI {
  /** Held right now. */
  isDown(action: ActionName): boolean;
  /** Went down this frame. Cleared by `endFrame()`. */
  pressed(action: ActionName): boolean;
  /** Accumulated mouse delta since the last frame, in radians of look. */
  readonly lookX: number;
  readonly lookY: number;
  /** -1..1 movement axes in local space. */
  readonly moveX: number;
  readonly moveZ: number;
  /** True while the pointer is locked to the canvas. */
  readonly locked: boolean;

  /** Resolves false when the browser refused; the caller must not resume without it. */
  requestLock(): Promise<boolean>;
  releaseLock(): void;
  /** Clears per-frame state. Called at the end of each tick by the game loop. */
  endFrame(): void;
  setSensitivity(v: number): void;
  setInvertY(v: boolean): void;
  dispose(): void;
}

/* ------------------------------------------------------------------ *
 * Rendering
 * ------------------------------------------------------------------ */

/** What the renderer exposes to the rest of the game. See `render/renderer.ts`. */
export interface RenderAPI {
  readonly scene: THREE.Scene;
  readonly camera: THREE.PerspectiveCamera;
  readonly renderer: THREE.WebGLRenderer;

  /** Tear down the previous level's meshes and build the new one. */
  buildLevel(level: GeneratedLevel): void;
  /** Remove all level geometry. */
  clearLevel(): void;

  /**
   * Position the camera from player state. Called every frame.
   *
   * `PlayerState` carries the vertical part of the head bob inside `eyeHeight`,
   * but the lateral sway and the strafe roll are camera-space and live on the
   * controller. `game.ts` passes them through; a renderer must still work when
   * they are omitted (tests, headless probes) and fall back to `player.pos`.
   */
  syncCamera(player: PlayerState, eye?: { x: number; y: number; z: number }, roll?: number): void;

  /** Create/update/remove the visual for an entity. */
  syncEntities(entities: readonly EntityState[]): void;
  /** Create/update/remove pickup meshes. */
  syncItems(items: readonly ItemSpot[]): void;

  /** 0..1. Drives grain, vignette, desaturation and warping. */
  setSanityEffect(v: number): void;
  /** 0..1 red flash on damage. */
  setDamageFlash(v: number): void;
  /** Toggle and aim the player's flashlight cone. */
  setFlashlight(on: boolean, charge: number): void;

  /**
   * Graphics settings changed. The settings system hands out a fresh object on
   * every edit, so the renderer must re-read rather than hold the original.
   */
  setSettings(settings: RenderSettings): void;

  /** Per-frame animation of lights, materials and post effects. */
  update(dt: number, ctx: GameContext): void;
  render(): void;
  resize(w: number, h: number): void;

  /** Approximate light level (0..1) at a world point. Entities and sanity read this. */
  lightLevelAt(pos: Vec2): number;

  dispose(): void;
}

/** Graphics options the settings menu can change. */
export interface RenderSettings {
  quality: 'low' | 'medium' | 'high';
  fov: number;
  /** Post-processing grain/vignette/aberration master switch. */
  postFx: boolean;
  headBob: boolean;
  resolutionScale: number;
}

/**
 * Everything a player might want to change about being inside a headset.
 *
 * Here rather than in `render/` because two subsystems need the same words for
 * it: `render/xrlocomotion.ts` owns the behaviour and persists it, and the
 * in-world settings screen under `ui/xr/` is where it is edited. Neither may
 * import the other, which is exactly what this file is for.
 *
 * The defaults are chosen for the most sensitive plausible player rather than
 * the toughest one: snap turning, and the comfort tunnel on. Nothing in here is
 * read on a flat frame.
 */
export interface XRComfort {
  /**
   * How the player travels, and the most consequential setting in the list.
   *
   * `dash` — repeated short hops behind a quick fade — is the default because
   * it is the only locomotion *both* target headsets can express: a Vision Pro
   * has no thumbstick to push. `arc` aims a longer jump and commits it on
   * release. `smooth` is stick locomotion, opt-in, and the one that makes
   * people ill.
   *
   * Every mode is billed from the same speed budget, so this changes how
   * movement feels and never how fast the player is. See `render/xrteleport.ts`.
   */
  move: 'dash' | 'arc' | 'smooth';
  /** Snap is the default, and the reason `render/xrlocomotion.ts` exists. */
  turn: 'snap' | 'smooth';
  /** Degrees per snap. */
  snapDegrees: number;
  /** Degrees per second while smooth turning. */
  smoothDegrees: number;
  /** 0 switches the comfort tunnel off entirely for players who do not want it. */
  vignette: number;
  /** Which hand holds the torch, and which hand's stick moves. */
  torchHand: 'left' | 'right';
  moveHand: 'left' | 'right';
}

/**
 * Read and edit the comfort settings.
 *
 * `render/xr.ts` implements it and persists every write itself, under its own
 * storage key — the comfort settings deliberately do not ride on the save blob,
 * so a headset can be made bearable without the settings system existing.
 */
export interface ComfortHost {
  readonly comfort: Readonly<XRComfort>;
  setComfort(patch: Partial<XRComfort>): void;
}

/* ------------------------------------------------------------------ *
 * Cross-system context
 * ------------------------------------------------------------------ */

/**
 * Passed to every system's `update`. Systems read what they need and mutate
 * only what they own.
 */
export interface GameContext {
  /** Seconds since the last frame, already clamped. */
  dt: number;
  /** Seconds since the run started. */
  time: number;
  /** Monotonically increasing frame counter. */
  frame: number;

  level: GeneratedLevel;
  /** Index into the level order, 0 = Level 0. */
  levelIndex: number;

  player: PlayerState;
  stats: SurvivalStats;
  inventory: readonly InventorySlot[];
  entities: readonly EntityState[];

  events: EventBus;
  input: InputAPI;
  audio: AudioAPI;
  render: RenderAPI;
  rng: RNG;

  /** True while a menu is up and the simulation is halted. */
  paused: boolean;
  /** Set once the player has died; the loop keeps rendering but stops simulating. */
  dead: boolean;
}

/* ------------------------------------------------------------------ *
 * Save data
 * ------------------------------------------------------------------ */

/** Persisted between sessions. See `save/storage.ts`. */
export interface SaveData {
  version: number;
  settings: GameSettings;
  progress: {
    /** Deepest level index reached, ever. */
    deepestLevel: number;
    /** Longest run in seconds. */
    bestTime: number;
    runs: number;
    deaths: number;
    /** Ids of notes the player has read, for the journal. */
    notesFound: string[];
  };
}

export interface GameSettings {
  masterVolume: number;
  muted: boolean;
  sensitivity: number;
  invertY: boolean;
  render: RenderSettings;
  subtitles: boolean;
}
